import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API, xhrJson } from '@/lib/api';
import { LOGO_URL } from '@/data/mockData';
import { Loader2, ShieldCheck, ShieldAlert } from 'lucide-react';

export default function ManagerLink() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, profile, loading: authLoading } = useAuth();
  const [phase, setPhase] = useState('checking'); // checking | valid_login | valid_redirect | invalid | no_perm
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setPhase('invalid'); return; }
      const { ok, data } = await xhrJson('GET', `${API}/api/manager/auth/link/${encodeURIComponent(token)}`);
      if (cancelled) return;
      if (!ok || !data?.ok) {
        setPhase('invalid');
        setError(data?.reason || 'invalid_token');
        return;
      }
      if (authLoading) return; // wait until auth resolves
      if (isLoggedIn) {
        const role = profile?.role;
        if (role === 'manager' || role === 'admin') {
          setPhase('valid_redirect');
          setTimeout(() => navigate('/gestor'), 600);
        } else {
          setPhase('no_perm');
        }
      } else {
        setPhase('valid_login');
      }
    })();
    return () => { cancelled = true; };
  }, [token, isLoggedIn, profile?.role, authLoading, navigate]);

  return (
    <div className="min-h-[100dvh] bg-[#f0efed] flex items-center justify-center p-6" data-testid="manager-link-landing">
      <div className="max-w-sm w-full text-center space-y-5 animate-fade-in">
        <div className="w-14 h-14 mx-auto rounded-full overflow-hidden">
          <img src={LOGO_URL} alt="Ruido.ai" className="w-full h-full object-cover scale-[1.15]" />
        </div>

        {phase === 'checking' && (
          <>
            <Loader2 className="w-5 h-5 mx-auto animate-spin text-gray-400" />
            <p className="text-sm text-gray-500">Verifying secure link…</p>
          </>
        )}

        {phase === 'valid_redirect' && (
          <>
            <ShieldCheck className="w-6 h-6 mx-auto text-emerald-500" />
            <p className="text-sm text-gray-700">Access confirmed. Taking you to the panel…</p>
          </>
        )}

        {phase === 'valid_login' && (
          <>
            <ShieldCheck className="w-6 h-6 mx-auto text-emerald-500" />
            <h2 className="text-lg font-medium text-gray-900 font-['Outfit']">Manager access confirmed</h2>
            <p className="text-sm text-gray-500">Sign in with your account to continue.</p>
            <button
              data-testid="goto-login"
              onClick={() => navigate('/')}
              className="text-xs text-gray-700 underline-offset-4 hover:underline"
            >
              Sign in
            </button>
          </>
        )}

        {phase === 'no_perm' && (
          <>
            <ShieldAlert className="w-6 h-6 mx-auto text-amber-500" />
            <h2 className="text-lg font-medium text-gray-900 font-['Outfit']">No manager permissions</h2>
            <p className="text-sm text-gray-500">Your account doesn't have a manager role. Contact the team to enable it.</p>
            <button
              data-testid="goto-client"
              onClick={() => navigate('/cliente')}
              className="text-xs text-gray-700 underline-offset-4 hover:underline"
            >
              Back to client portal
            </button>
          </>
        )}

        {phase === 'invalid' && (
          <>
            <ShieldAlert className="w-6 h-6 mx-auto text-rose-500" />
            <h2 className="text-lg font-medium text-gray-900 font-['Outfit']">Invalid link</h2>
            <p className="text-sm text-gray-500">The link expired or was rotated. Ask the team for a new one.</p>
            {error === 'no_token_configured' && (
              <p className="text-[11px] text-gray-400">(No token configured yet)</p>
            )}
            <button
              data-testid="goto-home"
              onClick={() => navigate('/')}
              className="text-xs text-gray-700 underline-offset-4 hover:underline"
            >
              Back to home
            </button>
          </>
        )}
      </div>
    </div>
  );
}
